import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { API_BASE_URL } from "@/config";

const schema = z.object({
  password: z.string().min(1, { message: "パスワードを入力してください" }),
  reason: z.string().max(1000).optional(),
});
type FormValues = z.infer<typeof schema>;

export default function AccountWithdrawForm({ onComplete }: { onComplete: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false); // ★ 成功ダイアログ制御

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        setError("ログインが必要です。");
        setSubmitting(false);
        return;
      }

      const res = await fetch(`${API_BASE_URL}/v1/user/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: values.password,
          reason: values.reason ?? "",
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.message || `退会に失敗しました (HTTP ${res.status})`);
        setSubmitting(false);
        return;
      }

      // ★ 成功：トークン等を消す
      try {
        localStorage.removeItem("token");
        document.cookie = 'ak_logged_in=; Path=/; Max-Age=0; Secure; SameSite=Lax';
        sessionStorage.clear();
      } catch {}

      setSubmitting(false);
      setDone(true); // ★ ダイアログを開く
    } catch (e: any) {
      setError(e?.message || "退会処理中にエラーが発生しました");
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setDone(false);
    onComplete(); // 親で遷移などを実施
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">退会手続き</h1>

      <div className="mb-6 text-sm leading-relaxed bg-gray-50 p-4 rounded-lg">
        <p className="mb-2">・退会すると、ログインできなくなります。</p>
        <p className="mb-2">・購入/申込履歴や各種データは、法令・規約に基づき一定期間保持される場合があります。</p>
        <p>・再開をご希望の場合でも、アカウントの復元はできない場合があります。</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block mb-1 font-medium">パスワード</label>
          <input
            type="password"
            className="w-full border rounded px-3 py-2"
            {...register("password")}
            autoComplete="current-password"
            placeholder="********"
          />
          {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block mb-1 font-medium">退会理由（任意）</label>
          <textarea
            className="w-full border rounded px-3 py-2 min-h-[96px]"
            placeholder="差し支えなければご記入ください（任意）"
            {...register("reason")}
          />
          {errors.reason && <p className="text-red-600 text-sm mt-1">{errors.reason.message}</p>}
        </div>

        {error && <div className="text-red-700 bg-red-50 border border-red-200 p-3 rounded">{error}</div>}

        <div className="pt-2">
          <button type="submit" disabled={submitting} className="m-btn primary">
            {submitting ? "処理中..." : "退会する"}
          </button>
        </div>
      </form>

      {/* ★ 成功ダイアログ */}
      {done && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[1000] flex items-center justify-center"
        >
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/40" />
          {/* panel */}
          <div className="relative bg-white rounded-xl shadow-xl p-6 w-[min(90vw,420px)]">
            <h2 className="text-lg font-semibold mb-2">退会しました</h2>
            <p className="text-sm text-gray-600 mb-6">
              ご利用ありがとうございました。
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={handleCloseDialog} className="m-btn primary">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
